import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import { canAccess } from "../commons/canAccess";
import { getSupabaseClient } from "./supabase";

const getBaseAuthProvider = () =>
  supabaseAuthProvider(getSupabaseClient(), {
    getIdentity: async () => {
      const sale = await getSale();

      if (sale == null) {
        throw new Error();
      }

      return {
        id: sale.id,
        fullName: `${sale.first_name} ${sale.last_name}`,
        avatar: sale.avatar?.src,
      };
    },
  });

// To speed up checks, we cache the initialization state
// and the current sale in the local storage. They are cleared on logout.
const IS_INITIALIZED_CACHE_KEY = "RaStore.auth.is_initialized";
const CURRENT_SALE_CACHE_KEY = "RaStore.auth.current_sale";

function getLocalStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

export async function getIsInitialized() {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(IS_INITIALIZED_CACHE_KEY);
  if (cachedValue != null) {
    return cachedValue === "true";
  }

  const { data } = await getSupabaseClient()
    .from("init_state")
    .select("is_initialized");
  const isInitialized = data?.at(0)?.is_initialized > 0;

  if (isInitialized) {
    storage?.setItem(IS_INITIALIZED_CACHE_KEY, "true");
  }

  return isInitialized;
}

const getSale = async () => {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(CURRENT_SALE_CACHE_KEY);
  if (cachedValue != null) {
    return JSON.parse(cachedValue);
  }

  const { data: dataSession, error: errorSession } =
    await getSupabaseClient().auth.getSession();

  // Shouldn't happen after login but just in case
  if (dataSession?.session?.user == null || errorSession) {
    return undefined;
  }

  const { data: dataSale, error: errorSale } = await getSupabaseClient()
    .from("sales")
    .select("id, first_name, last_name, avatar, administrator")
    .match({ user_id: dataSession?.session?.user.id })
    .single();

  // Shouldn't happen either as all users are sales but just in case
  if (dataSale == null || errorSale) {
    return undefined;
  }

  storage?.setItem(CURRENT_SALE_CACHE_KEY, JSON.stringify(dataSale));
  return dataSale;
};

/**
 * Read the `amr` (authentication method references) claim of a Supabase access
 * token. `app_metadata.provider` cannot be used to tell how the current session
 * was established: once a Google identity is linked to an existing password
 * account, `provider` stays "email" even for sessions opened through Google.
 * `amr` describes this session, and is "oauth" only for OAuth sign-ins.
 */
function wasSignedInWithOAuth(accessToken: string) {
  try {
    const [, payload] = accessToken.split(".");
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const amr = JSON.parse(json).amr as { method?: string }[] | undefined;
    return Array.isArray(amr) && amr.some((entry) => entry.method === "oauth");
  } catch {
    // A token we cannot read is not a token we can clear as safe.
    return true;
  }
}

/**
 * Google's `hd` parameter is a hint, not a guarantee: a determined user can
 * strip it from the authorize URL. Re-check the domain on our side and drop
 * any OAuth session whose email is outside the configured Workspace domain.
 * Email/password users are untouched, so guests on other domains still work.
 */
async function enforceGoogleWorkspaceDomain() {
  const domain =
    import.meta.env.VITE_GOOGLE_WORKPLACE_DOMAIN || "yeretagility.com";
  if (!domain) {
    return;
  }

  const { data } = await getSupabaseClient().auth.getSession();
  const session = data?.session;
  if (!session || !wasSignedInWithOAuth(session.access_token)) {
    return;
  }

  const email = session.user.email ?? "";
  if (email.toLowerCase().endsWith(`@${domain.toLowerCase()}`)) {
    return;
  }

  clearCache();
  await getSupabaseClient().auth.signOut();
  throw new Error(`Only @${domain} Google accounts can sign in.`);
}

function clearCache() {
  const storage = getLocalStorage();
  storage?.removeItem(IS_INITIALIZED_CACHE_KEY);
  storage?.removeItem(CURRENT_SALE_CACHE_KEY);
}

export const getAuthProvider = (): AuthProvider => {
  const baseAuthProvider = getBaseAuthProvider();
  return {
    ...baseAuthProvider,
    login: async (params) => {
      if (params.googleWorkspaceDomain) {
        // SAML SSO requires a paid Supabase plan, so we use plain Google
        // OAuth instead. `hd` asks Google to only offer accounts on our
        // Workspace domain; the domain is re-checked after the redirect in
        // `checkAuth`, and signups are disabled project-side so only users
        // already present in `auth.users` can get a session.
        const { error } = await getSupabaseClient().auth.signInWithOAuth({
          provider: "google",
          options: {
            redirectTo: window.location.origin,
            queryParams: {
              hd: params.googleWorkspaceDomain,
              prompt: "select_account",
            },
          },
        });
        if (error) {
          throw error;
        }
        return;
      }
      return baseAuthProvider.login(params);
    },
    logout: async (params) => {
      clearCache();
      return baseAuthProvider.logout(params);
    },
    checkAuth: async (params) => {
      // Users are on the set-password page, nothing to do
      if (
        window.location.pathname === "/set-password" ||
        window.location.hash.includes("#/set-password")
      ) {
        return;
      }
      // Users are on the forgot-password page, nothing to do
      if (
        window.location.pathname === "/forgot-password" ||
        window.location.hash.includes("#/forgot-password")
      ) {
        return;
      }
      // Users are on the sign-up page, nothing to do
      if (
        window.location.pathname === "/sign-up" ||
        window.location.hash.includes("#/sign-up")
      ) {
        return;
      }

      const isInitialized = await getIsInitialized();

      if (!isInitialized) {
        await getSupabaseClient().auth.signOut();
        throw {
          redirectTo: "/sign-up",
          message: false,
        };
      }

      await enforceGoogleWorkspaceDomain();

      return baseAuthProvider.checkAuth(params);
    },
    canAccess: async (params) => {
      const isInitialized = await getIsInitialized();
      if (!isInitialized) return false;

      // Get the current user
      const sale = await getSale();
      if (sale == null) return false;

      // Compute access rights from the sale role
      const role = sale.administrator ? "admin" : "user";
      return canAccess(role, params);
    },
    getAuthorizationDetails(authorizationId: string) {
      return getSupabaseClient().auth.oauth.getAuthorizationDetails(
        authorizationId,
      );
    },
    approveAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.approveAuthorization(
        authorizationId,
      );
    },
    denyAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.denyAuthorization(authorizationId);
    },
  };
};
